import React from 'react';
import styled from '@emotion/styled';
import ApplicationModel from '../../../../../../shared/models/applications/api/ApplicationModel';
import PmsLookupContainer from '../../../../../../shared/hooks/PmsLookupContainer';
import { PmsProviderModel } from '../../../../../../shared/models/organizations/PmsLookupModels';
import { HelpText, LookupTable, RefreshButton, Toolbar, personName, useAutoLoad } from './PmsLookupPanels';
import { DentalFormFieldVariables, isSandboxCloud9DefaultProvider } from '../dentalSandboxWarnings';
import { PillTone, SparkFormButton, StatusPill } from './sparkFormPills';
import { SparkFormBar, SparkFormRowConfirm, isUnsetFormValue, sameFormValue, useSparkFormField } from './sparkFormField';

// Comma-separated provider IDs the assistant may book with; empty means any provider
const ALLOWED_PROVIDER_IDS_VARIABLE = '{allowed-provider-ids}';

interface ProviderLookupProps {
    locationApps: ApplicationModel[];
    officeId: string;
    // Called after a Spark form save so the Office Connection tab can reload that location
    onFormSaved?: (appId: string, isShared: boolean) => void;
}

const parseIds = (value: string | null) => (value || '').split(',').map(id => id.trim()).filter(id => !isUnsetFormValue(id));

const nameFor = (providers: PmsProviderModel[] | undefined, id: string) => {
    const provider = providers?.find(p => sameFormValue(p.id, id));
    return provider ? personName(provider) : id;
};

// Cloud 9 books new patients with one default provider, stored as its GUID in {DefaultProviderId}
export const Cloud9ProvidersLookup: React.FC<ProviderLookupProps> = ({ locationApps, officeId, onFormSaved }) => {
    const pms = PmsLookupContainer.useContainer();
    useAutoLoad(pms.cloud9Employees, () => pms.loadCloud9Employees());
    const form = useSparkFormField(locationApps, officeId, DentalFormFieldVariables.defaultProviderId, onFormSaved);
    const { current, target, savingValue, pending } = form;
    const employees = pms.cloud9Employees.data;

    const status = !current ? (
        <StatusPill tone="missing">Default provider not set</StatusPill>
    ) : isSandboxCloud9DefaultProvider(current) ? (
        <StatusPill tone="warning" title="Still the Cloud 9 sandbox provider">Sandbox provider</StatusPill>
    ) : (
        <StatusPill tone="done" title={current}>Default: {nameFor(employees, current)}</StatusPill>
    );

    const renderAction = (row: PmsProviderModel) => {
        if (sameFormValue(row.id, current)) {
            return <StatusPill tone="done">✓ Default Provider</StatusPill>;
        }
        if (!target) return null;
        if (pending?.key === row.id) return <SparkFormRowConfirm form={form} rowKey={row.id} />;
        return (
            <SparkFormButton type="button" onClick={() => form.request(row.id, `Set ${personName(row) || row.id} as the Default Provider`)} disabled={!!savingValue || !!pending} title="Saves this GUID to the Spark form's Default Provider ID after you confirm">
                Set as Default Provider
            </SparkFormButton>
        );
    };

    return (
        <>
            <Toolbar>
                <SparkFormBar form={form} status={status} />
                <RefreshButton isLoading={pms.cloud9Employees.isLoading} onClick={() => pms.loadCloud9Employees(true)} />
            </Toolbar>
            <HelpText>New patient appointments are booked with the default provider.</HelpText>
            <LookupTable
                lookup={pms.cloud9Employees}
                columns={[
                    { header: 'Provider GUID', value: row => row.id, copyable: true },
                    { header: 'Code', value: row => row.code },
                    { header: 'Name', value: row => personName(row) },
                    { header: 'Type', value: row => row.type },
                    { header: 'Spark form', value: () => undefined, render: renderAction }
                ]}
            />
        </>
    );
};

// Other PMS limit booking to the providers listed in {allowed-provider-ids}
export const ProvidersLookup: React.FC<ProviderLookupProps> = ({ locationApps, officeId, onFormSaved }) => {
    const pms = PmsLookupContainer.useContainer();
    useAutoLoad(pms.providers, () => pms.loadProviders());
    const form = useSparkFormField(locationApps, officeId, ALLOWED_PROVIDER_IDS_VARIABLE, onFormSaved);
    const { current, target, savingValue, pending } = form;
    const allowed = parseIds(current);
    const providers = pms.providers.data;

    const isAllowed = (id: string) => allowed.some(allowedId => sameFormValue(allowedId, id));
    const toggle = (row: PmsProviderModel) => {
        const removing = isAllowed(row.id);
        const next = removing ? allowed.filter(id => !sameFormValue(id, row.id)) : [...allowed, row.id];
        const description = removing
            ? `Remove ${personName(row) || row.id} from the preferred providers`
            : `Make ${personName(row) || row.id} a preferred provider`;
        form.request(next.join(','), description, row.id);
    };

    const statusTone: PillTone = allowed.length > 0 ? 'done' : 'missing';
    const status = (
        <StatusPill tone={statusTone} title={allowed.length > 0 ? allowed.join(', ') : 'The assistant can book with any provider'}>
            {allowed.length === 0
                ? 'No preferred providers'
                : allowed.length <= 2 ? `Preferred: ${allowed.map(id => nameFor(providers, id)).join(', ')}` : `${allowed.length} preferred providers`}
        </StatusPill>
    );

    const renderAction = (row: PmsProviderModel) => {
        if (!target) return null;
        const isPending = pending?.key === row.id;
        const disabled = !!savingValue || !!pending;
        return isAllowed(row.id) ? (
            <AllowedCell>
                <StatusPill tone="done">✓ Preferred Provider</StatusPill>
                {isPending ? <SparkFormRowConfirm form={form} rowKey={row.id} /> : (
                    <RemoveButton type="button" onClick={() => toggle(row)} disabled={disabled} title="Removes this ID from the Spark form's Allowed Provider Ids after you confirm">
                        Remove Preferred Provider
                    </RemoveButton>
                )}
            </AllowedCell>
        ) : isPending ? <SparkFormRowConfirm form={form} rowKey={row.id} /> : (
            <SparkFormButton type="button" onClick={() => toggle(row)} disabled={disabled} title="Adds this ID to the Spark form's Allowed Provider Ids after you confirm">
                Make Preferred Provider
            </SparkFormButton>
        );
    };

    return (
        <>
            <Toolbar>
                <SparkFormBar form={form} status={status} />
                <RefreshButton isLoading={pms.providers.isLoading} onClick={() => pms.loadProviders(true)} />
            </Toolbar>
            <HelpText>
                {allowed.length === 0
                    ? 'No preferred providers, so the assistant can book with any provider. Making providers preferred limits booking to them.'
                    : 'The assistant only books with preferred providers.'}
                {' '}{pms.officeId ? 'Showing providers for the selected office.' : 'Showing providers across all offices.'}
            </HelpText>
            <LookupTable
                lookup={pms.providers}
                columns={[
                    { header: 'Provider ID', value: row => row.id, copyable: true },
                    { header: 'Name', value: row => personName(row) },
                    { header: 'Title', value: row => row.title },
                    { header: 'Office', value: row => row.officeId },
                    { header: 'Spark form', value: () => undefined, render: renderAction }
                ]}
            />
        </>
    );
};

const AllowedCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RemoveButton = styled.button`
    border: none;
    background: none;
    padding: 0;
    font-family: Muli;
    font-size: 12px;
    color: #c53030;
    cursor: pointer;
    text-decoration: underline;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;
